import type { Channel } from "@expert/shared-types";

export type Unchanneled<T, C extends Channel = Channel> = T extends { $channeled: Record<C, infer V> }
    ? Unchanneled<V, C>
    : T extends Array<infer U>
      ? Array<Unchanneled<U, C>>
      : T extends object
        ? { [K in keyof T]: Unchanneled<T[K], C> }
        : T;

export function unchannel<T, C extends Channel = Channel>(value: T, channel: C): Unchanneled<T, C> {
    if (typeof value !== "object" || value === null) return value as Unchanneled<T, C>;

    if (Array.isArray(value)) {
        return value.map((item) => unchannel(item, channel)) as Unchanneled<T, C>;
    }

    // If the object has a $channeled property and it's an object, return the value for the requested channel
    if ("$channeled" in value && typeof value.$channeled === "object" && value.$channeled !== null) {
        const $channeled = value.$channeled as Record<Channel, unknown>;
        if (channel in $channeled) {
            return unchannel($channeled[channel], channel) as Unchanneled<T, C>;
        } else {
            return undefined as Unchanneled<T, C>;
        }
    }

    const result: Record<string, unknown> = {};
    for (const key of Object.keys(value)) {
        result[key] = unchannel((value as Record<string, unknown>)[key], channel);
    }

    return result as Unchanneled<T, C>;
}
